import React, { useState, useEffect } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import { apiRequest } from '@/utils/api-request';
import {
  User,
  Bookmark,
  Home,
  Palette,
  ImageIcon,
  Users,
  Wallet,
  PenSquare,
  Building,
  Settings,
  ChevronRight,
  Briefcase,
  Star,
  MessageCircleQuestion,
  HelpCircle,
  GraduationCap,
  Camera,
  Bell,
  MapPin,
  Bus
} from 'lucide-react';

interface NavItem {
  name: string;
  path: string;
  icon: React.ReactNode;
  description?: string;
}

const ManageSchoolSidebar = () => {
  const location = useLocation();
  const { id } = useParams<{ id: string }>();
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [schoolName, setSchoolName] = useState<string>('');
  // null = not known yet (don't hide anything until we do); 0/1 = confirmed is_client value
  const [isClient, setIsClient] = useState<number | null>(null);

  useEffect(() => {
    // Read school name from localStorage on mount
    const savedSchoolName = localStorage.getItem(`school_name_${id}`);
    if (savedSchoolName) {
      setSchoolName(savedSchoolName);
    }

    // Listen for school name updates
    const handleSchoolNameUpdate = (event: CustomEvent) => {
      if (event.detail.id === id) {
        setSchoolName(event.detail.name);
      }
    };

    window.addEventListener('schoolNameUpdated', handleSchoolNameUpdate as EventListener);

    return () => {
      window.removeEventListener('schoolNameUpdated', handleSchoolNameUpdate as EventListener);
    };
  }, [id]);

  useEffect(() => {
    // Determine is_client status for this school so client-only sections can be hidden
    if (!id) return;
    let cancelled = false;

    const fetchIsClient = async () => {
      try {
        const response = await apiRequest(`/schools/${id}/basic-info`);
        if (!cancelled && response?.success && response?.data?.school_main) {
          setIsClient(response.data.school_main.is_client ?? 0);
        }
      } catch (err) {
        // Non-blocking — if this fails, leave sections visible rather than hiding them incorrectly
        console.warn('Failed to determine client status for sidebar filtering:', err);
      }
    };

    fetchIsClient();

    // Keep in sync if is_client is toggled on the Basic Info page in the same session
    const handleIsClientUpdate = (event: CustomEvent) => {
      if (event.detail.id === id) {
        setIsClient(event.detail.is_client ?? 0);
      }
    };
    window.addEventListener('schoolIsClientUpdated', handleIsClientUpdate as EventListener);

    return () => {
      cancelled = true;
      window.removeEventListener('schoolIsClientUpdated', handleIsClientUpdate as EventListener);
    };
  }, [id]);

  // These sections are client-exclusive — hidden for schools where is_client=0
  const CLIENT_ONLY_PATHS = new Set([
    'faqs',
    'blogs',
    'related_schools',
    'appears_in_schools',
    'target_locations',
    'bus_routes',
  ]);

  // Get client_id from localStorage user data
  const userStr = localStorage.getItem('crm_user');
  const user = userStr ? JSON.parse(userStr) : null;
  const clientId = user?.data?.data?.client_id_fk || user?.data?.client_id_fk || user?.client_id_fk || null;

  // Manage school navigation items
  const navItems: NavItem[] = [
    {
      name: 'Basic Info',
      path: 'basic',
      icon: <User className="h-4 w-4" />,
      description: 'School basic information'
    },
    {
      name: 'Admissions',
      path: 'admissions',
      icon: <Bookmark className="h-4 w-4" />,
      description: 'Admission details'
    },
    {
      name: 'Infrastructure',
      path: 'infrastructure',
      icon: <Home className="h-4 w-4" />,
      description: 'Facilities & infrastructure'
    },
    {
      name: 'Sports and Facilities',
      path: 'extra',
      icon: <Palette className="h-4 w-4" />,
      description: 'Sports and facilities'
    },
    {
      name: 'Media',
      path: 'media',
      icon: <ImageIcon className="h-4 w-4" />,
      description: 'Photos, videos & logos'
    },
    {
      name: 'Fee Structure',
      path: 'fee',
      icon: <Wallet className="h-4 w-4" />,
      description: 'Fee structure'
    },
    {
      name: 'Team',
      path: 'team',
      icon: <Users className="h-4 w-4" />,
      description: 'School team members'
    },
    {
      name: 'Notification Center',
      path: 'notifications',
      icon: <Bell className="h-4 w-4" />,
      description: 'Manage notification contacts'
    },
    {
      name: 'Campus Stories',
      path: 'stories',
      icon: <Camera className="h-4 w-4" />,
      description: 'Share campus stories'
    },
    {
      name: 'Insta Stories',
      path: 'instastories',
      icon: <Camera className="h-4 w-4" />,
      description: 'Share Instagram stories'
    },
    {
      name: 'Reviews',
      path: 'reviews',
      icon: <Star className="h-4 w-4" />,
      description: 'School reviews'
    },
    {
      name: 'Jobs',
      path: 'jobs',
      icon: <Briefcase className="h-4 w-4" />,
      description: 'Job postings & candidates'
    },
    {
      name: 'Q & A',
      path: 'qa',
      icon: <MessageCircleQuestion className="h-4 w-4" />,
      description: 'Questions & answers'
    },
     {
      name: 'Counsellors',
      path: 'counsellors',
      icon: <User className="h-4 w-4" />,
      description: 'counsellors & bookings'
    },
    {
      name: 'FAQs',
      path: 'faqs',
      icon: <HelpCircle className="h-4 w-4" />,
      description: 'Frequently asked questions'
    },
    {
      name: 'Blogs',
      path: 'blogs',
      icon: <PenSquare className="h-4 w-4" />,
      description: 'School blogs & articles'
    },
    {
      name: 'Related Schools',
      path: 'related_schools',
      icon: <Building className="h-4 w-4" />,
      description: 'Related schools'
    },
    {
      name: 'Appears In Schools',
      path: 'appears_in_schools',
      icon: <Building className="h-4 w-4" />,
      description: 'Schools where this school appears'
    },
    {
      name: 'Target Locations',
      path: 'target_locations',
      icon: <MapPin className="h-4 w-4" />,
      description: 'Ads target locations'
    },
    {
      name: 'Bus Routes',
      path: 'bus_routes',
      icon: <Bus className="h-4 w-4" />,
      description: 'School bus route areas'
    }
  ];

  // Conditional items - only show for client_id === 9
  const conditionalItems: NavItem[] = [
    
  ];

  // Combine items based on client_id
  const combinedNavItems = clientId === 9 ? [...navItems, ...conditionalItems] : navItems;

  // Hide client-exclusive sections when a non-client school (is_client=0) is viewed
  // from its own school-CRM login. Internal admin (client_id === 9) always sees everything.
  const allNavItems = (isClient === 0 && clientId !== 9)
    ? combinedNavItems.filter((item) => !CLIENT_ONLY_PATHS.has(item.path))
    : combinedNavItems;

  // Helper function to check if a path is active
  const isPathActive = (itemPath: string) => {
    const basePath = `/manage-school/${id}`;

    // If on base path, "basic" is active by default
    if (location.pathname === basePath || location.pathname === `${basePath}/`) {
      return itemPath === 'basic';
    }

    // Remove base path from current path
    const currentPath = location.pathname.replace(`${basePath}/`, '');

    // Check if current path starts with item path (for nested routes like jobs/*, media/*, blogs/*)
    // or exact match for non-nested routes
    return currentPath === itemPath || currentPath.startsWith(`${itemPath}/`);
  };

  // Helper function to generate the full href path
  const getFullPath = (path: string) => {
    return `/manage-school/${id}${path ? `/${path}` : ''}`;
  };

  return (
    <div className={`${isCollapsed ? 'w-16' : 'w-72'} h-full bg-white dark:bg-gray-900 border-r border-gray-200 dark:border-gray-700 transition-all duration-300 flex flex-col`}>
      {/* Header */}
      <div className="p-4 border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-center justify-between">
          <div className={`flex items-center space-x-3 ${isCollapsed ? 'hidden' : ''}`}>
            <div className="bg-brandColor p-2 rounded-lg">
              <Settings className="h-5 w-5 text-white" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Manage School</h2>
              <p className="text-xs text-gray-500 dark:text-gray-400">School ID: {id}</p>
            </div>
          </div>
          <button
            onClick={() => setIsCollapsed(!isCollapsed)}
            className="p-1.5 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
            title={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            <ChevronRight className={`h-4 w-4 text-gray-500 transition-transform ${!isCollapsed ? 'rotate-180' : ''}`} />
          </button>
        </div>
      </div>

      {/* School Name Display */}
      {!isCollapsed && schoolName && (
        <div className="px-3 py-3 border-b border-gray-200 dark:border-gray-700 bg-blue-50/60 dark:bg-blue-900/20">
          <div className="flex items-center gap-2">
            <GraduationCap className="h-4 w-4 text-brandColor flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-gray-900 dark:text-white truncate" title={schoolName}>
                {schoolName}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto p-3 space-y-1">
        {allNavItems.map((item) => {
          const isActive = isPathActive(item.path);

          return (
            <Link
              key={item.path}
              to={getFullPath(item.path)}
              className={`
                relative group flex items-center px-3 py-2 rounded-lg transition-all duration-200
                ${isActive
                  ? 'bg-gray-50 dark:bg-gray-800 text-brandColor dark:text-brandColor border-l-4 border-brandColor'
                  : 'hover:bg-gray-50 dark:hover:bg-gray-800 text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white'
                }
                ${!isCollapsed ? 'space-x-3' : 'justify-center'}
              `}
            >
              {/* Icon */}
              <div className={`flex-shrink-0 ${isActive ? 'text-brandColor dark:text-brandColor' : 'text-gray-400 group-hover:text-gray-600 dark:group-hover:text-gray-300'}`}>
                {item.icon}
              </div>

              {/* Content */}
              {!isCollapsed && (
                <div className="flex-1">
                  <span className="text-sm font-medium">{item.name}</span>
                  {item.description && (
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{item.description}</p>
                  )}
                </div>
              )}

              {/* Tooltip for collapsed state */}
              {isCollapsed && (
                <div className="absolute left-full ml-2 px-2 py-1 bg-gray-900 text-white text-xs rounded opacity-0 group-hover:opacity-100 pointer-events-none whitespace-nowrap z-10">
                  {item.name}
                </div>
              )}
            </Link>
          );
        })}
      </nav>
    </div>
  );
};

export default ManageSchoolSidebar;
